import { useOutletContext } from "react-router-dom";
import { Users, User, Crown, Shield } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const HERO_IMAGE = "https://images.pexels.com/photos/7648071/pexels-photo-7648071.jpeg";

// Placeholder avatar for members without photo
const DEFAULT_AVATAR = "https://images.unsplash.com/photo-1511367461989-f85a21fda167?w=150&h=150&fit=crop&crop=face";

const orgStructure = {
  bestuur: [
    { 
      function: "Voorzitter", 
      name: "Tom Verstraeten",
      photo: null,
      description: "Algemene leiding en vertegenwoordiging van de club" 
    },
    { 
      function: "Ondervoorzitter", 
      name: "Kris Baert",
      photo: null,
      description: "Ondersteuning voorzitter en projectcoördinatie" 
    },
    { 
      function: "Secretaris", 
      name: "Sarah De Wit",
      photo: null,
      description: "Administratie en communicatie" 
    },
    { 
      function: "Penningmeester", 
      name: "Johan Peeters",
      photo: null,
      description: "Financiën en boekhouding" 
    },
  ],
  technisch: [
    { 
      function: "Technisch Directeur", 
      name: "Marc Van den Berg",
      photo: null,
      description: "Sportief beleid en opleiding" 
    },
    { 
      function: "Hoofdcoach Eerste ploeg", 
      name: "Diogo Santos",
      photo: null,
      description: "Training en begeleiding eerste elftal" 
    },
    { 
      function: "Coördinator Jeugd", 
      name: "Peter Janssens",
      photo: null,
      description: "Jeugdopleiding en ontwikkeling" 
    },
    { 
      function: "Keeperstrainer", 
      name: "Bram Claeys",
      photo: null,
      description: "Specifieke keeperstraining alle teams" 
    },
  ],
  operationeel: [
    { 
      function: "Eventmanager", 
      name: "Lisa Vermeersch",
      photo: null,
      description: "Organisatie wedstrijden en evenementen" 
    },
    { 
      function: "Communicatie", 
      name: "Emma De Smedt",
      photo: null,
      description: "Social media en pers" 
    },
    { 
      function: "Sponsoring", 
      name: "Pieter Van Acker",
      photo: null,
      description: "Partnerschappen en sponsorwerving" 
    },
    { 
      function: "Vrijwilligerscoördinator", 
      name: "An Willems",
      photo: null,
      description: "Beheer vrijwilligersnetwerk" 
    },
  ],
};

export default function Organigram() {
  const context = useOutletContext();
  const isDark = context?.isDark ?? true;

  const getIcon = (section) => {
    switch(section) {
      case 'bestuur': return Crown;
      case 'technisch': return Shield;
      case 'operationeel': return Users;
      default: return User;
    }
  };

  const getTitle = (section) => {
    switch(section) {
      case 'bestuur': return 'Bestuur';
      case 'technisch': return 'Technische Staf';
      case 'operationeel': return 'Operationeel';
      default: return section;
    }
  };

  const getColor = (section) => {
    switch(section) {
      case 'bestuur': return isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-600';
      case 'technisch': return isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600';
      case 'operationeel': return isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600';
      default: return isDark ? 'bg-slate-500/20 text-slate-400' : 'bg-slate-100 text-slate-600';
    }
  };

  return (
    <div className="min-h-screen" data-testid="organigram-page">
      {/* Hero */}
      <section className="relative h-[40vh] min-h-[300px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-end pb-12">
          <div>
            <p className="text-[hsl(48,96%,53%)] font-medium mb-2">Club Info</p>
            <h1 className="text-4xl md:text-5xl font-black text-white font-['Chivo']">ORGANIGRAM</h1>
            <p className="text-slate-300 mt-2">De structuur van onze club</p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction */}
        <Card className={`mb-12 ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}>
          <CardContent className="py-8">
            <p className={`text-lg leading-relaxed text-center max-w-3xl mx-auto ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              Futsal Gent wordt gerund door een toegewijd team van bestuursleden, trainers en 
              vrijwilligers. Hieronder vind je een overzicht van de verschillende afdelingen 
              en hun verantwoordelijkheden binnen onze club.
            </p>
          </CardContent>
        </Card>

        {/* Org Structure */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {Object.entries(orgStructure).map(([section, roles]) => {
            const Icon = getIcon(section);
            const colorClass = getColor(section);
            return (
              <Card key={section} className={`${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-lg ${colorClass.split(' ')[0]}`}>
                      <Icon className={`h-6 w-6 ${colorClass.split(' ')[1]}`} />
                    </div>
                    <CardTitle className={`font-['Chivo'] text-xl ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {getTitle(section)}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {roles.map((role, index) => (
                      <div 
                        key={index}
                        className={`p-4 rounded-lg ${isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`}
                      >
                        <div className="flex items-center gap-4">
                          {/* Photo */}
                          <div className="shrink-0">
                            <img 
                              src={role.photo || DEFAULT_AVATAR} 
                              alt={role.name}
                              className="w-14 h-14 rounded-full object-cover border-2 border-white/20"
                            />
                          </div>
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <h4 className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              {role.name}
                            </h4>
                            <p className={`text-sm font-medium ${colorClass.split(' ')[1]}`}>
                              {role.function}
                            </p>
                            <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                              {role.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA */}
        <Card className={`mt-12 ${isDark ? 'bg-gradient-to-r from-blue-900/50 to-slate-900/50 border-white/10' : 'bg-gradient-to-r from-blue-50 to-slate-50 border-slate-200 shadow-md'}`}>
          <CardContent className="py-12 text-center">
            <h3 className={`text-2xl font-black font-['Chivo'] mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Wil je deel uitmaken van ons team?
            </h3>
            <p className={`max-w-2xl mx-auto ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              We zijn altijd op zoek naar enthousiaste vrijwilligers en medewerkers. 
              Bekijk onze vacatures of neem contact met ons op!
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
